import { formatCallDuration, formatCallDurationClock } from "../utils/callDuration";
import { FiTrash2 } from "react-icons/fi";

function formatWhen(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(value);
  }
}

/** Per-client call time summary (admin Agent Reports). */
export function ClientCallSummaryTable({ rows = [], className = "" }) {
  if (!rows.length) {
    return (
      <p className={`text-xs text-gray-400 py-3 text-center ${className}`}>
        No client call time recorded for this period.
      </p>
    );
  }

  return (
    <div className={`overflow-x-auto rounded-lg border border-gray-200 bg-white ${className}`}>
      <table className="min-w-full text-xs">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left font-semibold text-gray-500">Client</th>
            <th className="px-3 py-2 text-left font-semibold text-gray-500">Email</th>
            <th className="px-3 py-2 text-center font-semibold text-gray-500">Calls</th>
            <th className="px-3 py-2 text-right font-semibold text-gray-500">Total time</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.userId} className="border-t border-gray-100">
              <td className="px-3 py-2 font-medium text-gray-900">{row.customerName}</td>
              <td className="px-3 py-2 text-gray-500">{row.customerEmail || "—"}</td>
              <td className="px-3 py-2 text-center">{row.callsCount}</td>
              <td className="px-3 py-2 text-right font-semibold text-gray-800">
                {formatCallDuration(row.totalDurationSec)}
                <span className="block text-[10px] font-normal text-gray-400">
                  {Number(row.totalMinutes || 0).toFixed(1)} min
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Full call log records (CSR performance, expandable agent details). */
export function CallRecordsTable({
  records = [],
  showAgent = false,
  canDelete = false,
  deletingId = null,
  onDelete,
  emptyMessage = "No call records for this period.",
  className = "",
}) {
  if (!records.length) {
    return <p className={`text-xs text-gray-400 py-4 text-center ${className}`}>{emptyMessage}</p>;
  }

  return (
    <div className={`overflow-x-auto rounded-lg border border-gray-200 bg-white ${className}`}>
      <table className="min-w-full text-xs">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left font-semibold text-gray-500">Date & time</th>
            <th className="px-3 py-2 text-left font-semibold text-gray-500">Client</th>
            <th className="px-3 py-2 text-left font-semibold text-gray-500">Email</th>
            {showAgent ? (
              <th className="px-3 py-2 text-left font-semibold text-gray-500">CSR</th>
            ) : null}
            <th className="px-3 py-2 text-left font-semibold text-gray-500">Duration</th>
            <th className="px-3 py-2 text-left font-semibold text-gray-500">Source</th>
            <th className="px-3 py-2 text-left font-semibold text-gray-500">Notes</th>
            {canDelete ? (
              <th className="px-3 py-2 text-center font-semibold text-gray-500 w-16">Delete</th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {records.map((call) => (
            <tr key={call.id} className="border-t border-gray-100 hover:bg-gray-50/50">
              <td className="px-3 py-2 whitespace-nowrap text-gray-800">{formatWhen(call.startedAt)}</td>
              <td className="px-3 py-2 font-medium text-gray-900">{call.customerName || "—"}</td>
              <td className="px-3 py-2 text-gray-500 max-w-[160px] truncate">{call.customerEmail || "—"}</td>
              {showAgent ? (
                <td className="px-3 py-2 text-gray-600">{call.agentName || "—"}</td>
              ) : null}
              <td className="px-3 py-2 font-semibold text-gray-900 whitespace-nowrap">
                {formatCallDurationClock(call.durationSec)}
                <span className="block text-[10px] font-normal text-gray-400">
                  {formatCallDuration(call.durationSec)}
                </span>
              </td>
              <td className="px-3 py-2">
                <span
                  className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                    call.source === "auto"
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-blue-100 text-blue-800"
                  }`}
                >
                  {call.sourceLabel}
                </span>
              </td>
              <td className="px-3 py-2 text-gray-500 max-w-[140px] truncate">{call.notes || "—"}</td>
              {canDelete ? (
                <td className="px-3 py-2 text-center">
                  <button
                    type="button"
                    onClick={() => onDelete?.(call)}
                    disabled={deletingId === call.id}
                    className="inline-flex items-center justify-center p-1.5 rounded-lg text-rose-600 hover:bg-rose-50 disabled:opacity-40"
                    title="Delete record (Developer only)"
                  >
                    <FiTrash2 size={14} />
                  </button>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
